import { t } from '@lingui/macro';
import React, { FunctionComponent, HTMLAttributes, useState, useEffect, useCallback } from 'react';

import { useFetch } from '@api/ApiHelper';
import { CustomRoleWithUsage } from '@api/v4/organizations/custom_permissions';
import { PlanGetResponse } from '@api/v4/resources/plan';
import { Brandfolder } from '@components/bulk_management/user-settings/resource-sidebar';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { DialogProvider } from '@components/common/providers/DialogContextProvider';
import {
  LimitsApiDataResponse,
  ResourceUsageResponse
} from '@components/limits/customLimitsTypes';
import UsageInfoCard from '@components/limits/usage_info_card/UsageInfoCard';

import { AddCustomRoleCard } from './AddCustomRoleCard';
import { CreateCustomRolePage } from './CreateCustomRolePage';
import { CustomRoleManagerWrapper } from './CustomRoleManager';
import { findParentBrandfolderKey } from './helpers';

const PENDING_INVITATIONS = {
  singular: t`pending invitation`,
  plural: t`pending invitations`,
};

const checkPendingInvitationsText = (value: number): string => {
  return value === 1 ? PENDING_INVITATIONS.singular : PENDING_INVITATIONS.plural;
};

const createWarningFlag = (invites: number, usage: number, limit: number): string =>
  limit - usage === invites + 1 && t`1 left`;

const createDangerFlag = (invites: number, usage: number, limit: number): string =>
  (usage >= limit || invites >= limit - usage) &&
  t`0 left`;

interface CustomRoleUsageCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  organizationAdmin?: boolean;
  organizationKey?: string;
  organizationOwner?: boolean;
  viewOnly?: boolean;
  hasSetLimits?: boolean;
  resourceType?: string;
  resourceKey?: string;
  brandfolders?: Brandfolder[];
  customRole: CustomRoleWithUsage;
  refreshCustomRole: () => Promise<void>
  resourceUsage: ResourceUsageResponse
  planResponse: PlanGetResponse
  setLimitsAction?: () => void;
  setReloadUsers?: SetStateDispatch<boolean>;
  customRoleLimitsLoader?: React.MutableRefObject<(() => void) | null>;
}

function getOpenValue(): boolean {
  const storageKey = `bf_new_custom_role_created_${BFG.organization_key}`;
  const value = window.localStorage?.getItem(storageKey);
  return value === '1';
}

export const CustomRoleUsageCard: FunctionComponent<CustomRoleUsageCardProps> = ({
  organizationAdmin = undefined,
  organizationKey = undefined,
  organizationOwner = undefined,
  viewOnly = false,
  className = '',
  hasSetLimits = false,
  resourceType = undefined,
  resourceKey = undefined,
  brandfolders = [],
  customRole,
  refreshCustomRole,
  resourceUsage,
  planResponse,
  setLimitsAction = undefined,
  setReloadUsers = undefined,
  customRoleLimitsLoader = undefined,
  ...props
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customLimit, setCustomLimit] = useState<number>(0);
  const [customUsage, setCustomUsage] = useState<number>(0);

  const orgKey = organizationKey || BFG.organization_key;
  const customPermissionKey = customRole?.key;

  const [open, setOpen] = useState(getOpenValue());

  const limitsFeatureOn =
    BFG.hasFeature('resource_level_limit') && organizationAdmin;
  const combinedRoleLimitsFeatureOn = BFG.hasFeature('combined_role_limits_rollout');

  const currentResourceType = resourceType || BFG.resource?.type || 'organization';
  const currentResourceKey = resourceKey || BFG.resource?.key;

  // For collection resources, resolve limits at the parent brandfolder level
  const parentBrandfolderKey =
    currentResourceType === 'collection'
      ? findParentBrandfolderKey(currentResourceKey, brandfolders)
      : undefined;
  const limitsResourceType =
    currentResourceType === 'collection' && parentBrandfolderKey
      ? 'brandfolder'
      : currentResourceType;
  const limitsResourceKey =
    currentResourceType === 'collection'
      ? (parentBrandfolderKey || currentResourceKey)
      : currentResourceKey;

  const customLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: customPermissionKey
      ? `/api/v4/organizations/${orgKey}/limits/${limitsResourceType}/collaborator/` +
        `${customPermissionKey}?include=resource`
      : '',
    fetchOnMount: false,
  });

  const loadCustomLimits = useCallback(() => {
    if (customPermissionKey && orgKey) {
      customLimitsFetch.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPermissionKey, orgKey, customLimitsFetch]);

  // Register the loadCustomLimits function with the parent ref
  useEffect(() => {
    if (customRoleLimitsLoader) {
      customRoleLimitsLoader.current = loadCustomLimits;
    }
    return () => {
      if (customRoleLimitsLoader) {
        customRoleLimitsLoader.current = null;
      }
    };
  }, [loadCustomLimits, customRoleLimitsLoader]);

  useEffect(() => {
    if (customPermissionKey && orgKey && organizationAdmin) {
      customLimitsFetch.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPermissionKey, orgKey, limitsResourceType, organizationAdmin]);

  useEffect(() => {
    if (!customPermissionKey) {
      setCustomLimit(0);
      setCustomUsage(0);
      return;
    }

    const customRoleUsageCount =
      Array.isArray(resourceUsage?.data) ?
        resourceUsage?.data.find((res) => res.id === resourceKey)?.attributes?.current_usage?.[customPermissionKey] :
        resourceUsage?.data.attributes?.current_usage?.[customPermissionKey];

    if (limitsFeatureOn) {
      const resource = customLimitsFetch.response?.data?.find(
        (limit) => limitsResourceKey === limit.relationships.resource.data.id
      )?.attributes;
      const resourceLimit = resource?.value || 0;
      setCustomLimit(resourceLimit);
      setCustomUsage(customRoleUsageCount || 0);
    } else {
      const orgLimit = combinedRoleLimitsFeatureOn
        ? planResponse?.data?.attributes.limits.combined_roles
        : planResponse?.data?.attributes.limits.collaborators; // Fallback to collaborator limit
      setCustomLimit(orgLimit || 0);
      setCustomUsage(customRoleUsageCount || 0);
    }
  }, [
    customPermissionKey,
    limitsFeatureOn,
    combinedRoleLimitsFeatureOn,
    customLimitsFetch.response,
    planResponse,
    currentResourceKey,
    limitsResourceKey,
    brandfolders,
    customRole?.key,
    resourceUsage?.data,
    resourceKey
  ]);

  const handleCreateRole = (): void => {
    setShowCreateForm(true);
  };

  const handleBackFromCreate = (): void => {
    setShowCreateForm(false);
    void refreshCustomRole();
    setOpen(getOpenValue());
  };

  const handleDelete = (): void => {
    void refreshCustomRole(); // Refresh after delete to update UI state
    setReloadUsers(true);
    document.getElementById('custom-role-usage-card')?.classList.remove('has-custom-role');
  };

  const handleSave = (): void => {
    document.getElementById('custom-role-usage-card')?.classList.add('has-custom-role');
  };

  // Show create form if user clicked to create/edit
  if (showCreateForm) {
    return (
      <I18nProviderWrapper>
        <DialogProvider>
          <CustomRoleManagerWrapper>
            <CreateCustomRolePage
              existingRole={customRole}
              onBack={handleBackFromCreate}
              onDelete={handleDelete}
              onSave={handleSave}
              organizationKey={BFG.organization_key}
              userCount={customUsage}
            />
          </CustomRoleManagerWrapper>
        </DialogProvider>
      </I18nProviderWrapper>
    );
  }

  // Show "Add Custom Role" card if no role exists
  if (!customRole && currentResourceType === 'organization') {
    return (
      <I18nProviderWrapper>
        <div
          className={className ? className : 'alert well-sm level-limits-card j-custom-role-card'}
          style={{ ...props.style, order: 4, margin: 0, flex: '0 0 205px' }}
        >
          <AddCustomRoleCard onNavigateToCreate={handleCreateRole} />
        </div>
      </I18nProviderWrapper>
    );
  }

  if (customRole) {
    let currentUsage = customUsage;
    let displayLimit: string | number = customLimit;

    if (limitsFeatureOn) {
      displayLimit = customLimit || 0;
    } else {
      currentUsage = planResponse?.data?.attributes.custom_permission_counts[customRole.key] || 0;
      displayLimit = customLimit >= 0 ? customLimit : '∞';
    }

    const usage = {
      current: currentUsage,
      limit: displayLimit,
    };

    // Compute amount unallocated across all resources for combined roles
    const maxTotal = planResponse?.data?.attributes.limits.combined_roles || 0;
    const customRoleInviteCount = limitsFeatureOn
      ? Array.isArray(resourceUsage?.data)
        ? resourceUsage?.data
          .find((res) => res.id === resourceKey)
          ?.attributes?.current_invites?.[customPermissionKey]
        : resourceUsage?.data.attributes?.current_invites?.[customPermissionKey]
      : planResponse?.data?.attributes.custom_permission_invites?.[customRole.key];
    return (
      <I18nProviderWrapper>
        <div
          {...props}
          style={{
            order: customRole ? 1 : 2,
            flex: customRole ? '1' : 'auto',
          }}
        >
          <UsageInfoCard
            customClass="user-limit-card"
            dangerAlert={createDangerFlag(customRoleInviteCount, currentUsage, customLimit)}
            editCapabilitiesTooltip={{
              open,
              onClose: () => {
                try {
                  const storageKey = `bf_new_custom_role_created_${orgKey}`;
                  window.localStorage?.removeItem(storageKey);
                  setOpen(false);
                } catch (_) { /* no-op */ }
              },
              message: t`Now you can start sending out invitations and assigning people to this new role.`
            }}
            editEnabled={resourceType === 'organization' && organizationOwner}
            hasSetLimits={hasSetLimits}
            icon="Person"
            isEnabled={true}
            newRoleTooltip={{
              open,
              onClose: () => {
                try {
                  const storageKey = `bf_new_custom_role_created_${orgKey}`;
                  window.localStorage?.removeItem(storageKey);
                  setOpen(false);
                } catch (_) { /* no-op */ }
              },
              maxTotal
            }}
            openEditDialog={handleCreateRole}
            orgKey={organizationKey}
            setLimitsAction={setLimitsAction || loadCustomLimits}
            subtitle={`${customRoleInviteCount || 0} ${checkPendingInvitationsText(customRoleInviteCount || 0)}`}
            title={customRole.label}
            usage={usage}
            warningAlert={createWarningFlag(customRoleInviteCount, currentUsage, customLimit)}
          />
        </div>
      </I18nProviderWrapper>
    );
  }

  // If view_only and no role exists, show nothing
  return null;
};
